"use client";

import { motion } from "framer-motion";
import { Box, Check, Package, ShoppingCart } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

const container = {
	hidden: { opacity: 0 },
	show: {
		opacity: 1,
		transition: {
			staggerChildren: 0.05,
		},
	},
};

const item = {
	hidden: { opacity: 0, y: 15 },
	show: {
		opacity: 1,
		y: 0,
		transition: {
			duration: 0.25,
		},
	},
};

function ProductCardSkeleton() {
	return (
		<motion.div
			variants={item}
			className="group relative flex flex-col rounded-xl bg-card border-2 transition-all overflow-hidden border-border shadow-sm min-h-0"
		>
			<div className="text-left w-full h-full p-0 flex flex-col items-stretch">
				<div className="relative aspect-4/3 w-full bg-secondary overflow-hidden shrink-0">
					<Skeleton className="h-full w-full rounded-none" />
					<div className="absolute top-2 left-2 flex flex-col gap-1">
						<Skeleton className="h-4 w-16 rounded-full" />
						<Skeleton className="h-4 w-10 rounded-full" />
					</div>
					<Skeleton className="absolute top-2 right-2 h-7 w-7 rounded-full" />
				</div>

				<div className="flex flex-1 flex-col p-3 w-full">
					<Skeleton className="h-4 sm:h-5 w-3/4 mb-1" />
					<Skeleton className="h-4 sm:h-5 w-2/3 mb-1" />
					<div className="flex flex-wrap gap-1 mb-1">
						<Skeleton className="h-3 w-12 rounded-full" />
						<Skeleton className="h-3 w-16 rounded-full" />
						<Skeleton className="h-3 w-12 rounded-full" />
					</div>

					<div className="flex flex-wrap items-center gap-1 mt-auto pt-1">
						<Skeleton className="h-3 sm:h-4 w-12" />
						<Skeleton className="h-3 sm:h-4 w-14" />
						<Skeleton className="h-3 sm:h-4 w-16" />
					</div>
				</div>
			</div>

			<div className="px-3 pb-3 w-full">
				<div className="pt-2 border-t border-border/50">
					<div className="flex items-center gap-1.5">
						<Skeleton className="h-8 w-8 rounded-full" />
						<Skeleton className="h-8 flex-1 rounded-lg" />
						<Skeleton className="h-8 w-8 rounded-full" />
					</div>
				</div>
			</div>
		</motion.div>
	);
}

export function BuildABoxSkeleton() {
	return (
		<div className="max-w-450 mx-auto px-4 pb-36 sm:pb-32 lg:pb-12 animate-in fade-in duration-300">
			<div className="text-center py-8 sm:py-12 md:py-16 space-y-3">
				<h1 className="text-3xl sm:text-5xl md:text-6xl font-black tracking-tight uppercase">
					Build Your Box
				</h1>

				<div className="flex justify-center gap-4 sm:gap-8 mt-8 overflow-x-auto pb-2 no-scrollbar">
					{[
						{ icon: Box, label: "1. Size", desc: "12-80 meals" },
						{ icon: Check, label: "2. Pick", desc: "Mix & match" },
						{ icon: Package, label: "3. Ship", desc: "We Pack and Ship" },
					].map((step) => (
						<div
							key={step.label}
							className="flex flex-col items-center gap-2 min-w-20"
						>
							<div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary border border-primary/20">
								<step.icon className="w-5 h-5" />
							</div>
							<div className="text-center">
								<p className="font-bold text-sm">{step.label}</p>
								<p className="text-xs text-muted-foreground">{step.desc}</p>
							</div>
						</div>
					))}
				</div>
			</div>

			<div className="grid gap-6 lg:gap-10 lg:grid-cols-12 items-start">
				<div className="lg:col-span-8 space-y-8">
					<section className="lg:hidden">
						<div className="flex items-center gap-2 mb-4">
							<span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground font-bold text-xs">
								0
							</span>
							<h2 className="text-lg sm:text-xl font-bold">
								Choose Purchase Type
							</h2>
						</div>
						<div className="bg-muted/30 p-1 rounded-xl border border-border/50 space-y-1">
							<Skeleton className="h-20 w-full rounded-lg" />
							<Skeleton className="h-14 w-full rounded-lg" />
						</div>
						<div className="mt-3 px-3 space-y-2">
							<Skeleton className="h-9 w-full rounded-md" />
							<Skeleton className="h-3 w-3/4" />
						</div>
					</section>

					<section>
						<div className="flex items-center gap-2 mb-4">
							<span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground font-bold text-xs">
								1
							</span>
							<h2 className="text-lg sm:text-xl font-bold">
								Select Your Box Size
							</h2>
						</div>
						<div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
							{[12, 20, 40, 60, 80].map((size) => (
								<div key={size} className="relative">
									{size === 40 && (
										<Skeleton className="absolute top-0 right-0 h-5 w-10 rounded-bl-xl rounded-tr-lg z-10" />
									)}
									<Skeleton className="h-32 sm:h-36 rounded-xl" />
								</div>
							))}
						</div>
					</section>

					<section>
						<div className="flex items-center gap-2 mb-4">
							<span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground font-bold text-xs">
								2
							</span>
							<h2 className="text-lg sm:text-xl font-bold">
								Choose Your Meals
							</h2>
						</div>

						<div className="flex flex-col gap-3 mb-6">
							<Skeleton className="h-10 w-full rounded-lg" />
							<div className="flex flex-wrap gap-2">
								{["f0", "f1", "f2", "f3", "f4"].map((k) => (
									<Skeleton key={k} className="h-9 w-24 rounded-lg" />
								))}
							</div>
						</div>

						<div className="flex items-center justify-between mb-4">
							<Skeleton className="h-4 w-40" />
							<Skeleton className="h-4 w-24" />
						</div>

						<motion.div
							variants={container}
							initial="hidden"
							animate="show"
							className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4"
						>
							{Array.from({ length: 9 }).map((_, i) => (
								<ProductCardSkeleton key={`skeleton-${i.toString()}`} />
							))}
						</motion.div>
					</section>
				</div>

				<div className="hidden lg:block lg:col-span-4">
					<div className="rounded-2xl bg-card border shadow-lg sticky top-24 p-5">
						<h2 className="text-lg font-bold mb-4 flex items-center gap-2">
							<ShoppingCart className="w-5 h-5" />
							Your Box
						</h2>

						<div className="flex items-center gap-2 mb-4">
							<div className="p-2 rounded-lg bg-primary">
								<Package className="w-4 h-4 text-white" />
							</div>
							<div>
								<Skeleton className="h-5 w-24 mb-1" />
								<Skeleton className="h-3 w-20" />
							</div>
						</div>

						<Skeleton className="h-2.5 w-full rounded-full mb-4" />

						<div className="text-center py-8 px-4 border-2 border-dashed rounded-xl bg-muted/30">
							<Box className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
							<p className="text-sm font-medium text-muted-foreground">
								Your box is empty
							</p>
							<p className="text-xs text-muted-foreground/70 mt-1">
								Select meals to fill it up
							</p>
						</div>

						<div className="bg-muted/30 p-1 rounded-xl border border-border/50 mt-4 space-y-1">
							<Skeleton className="h-20 w-full rounded-lg" />
							<Skeleton className="h-14 w-full rounded-lg" />
						</div>

						<div className="border-t border-dashed mt-4 pt-4 space-y-2">
							<div className="flex justify-between">
								<Skeleton className="h-4 w-24" />
								<Skeleton className="h-4 w-24" />
							</div>
							<div className="flex justify-between">
								<Skeleton className="h-4 w-24" />
								<Skeleton className="h-4 w-16" />
							</div>
							<div className="flex justify-between pt-2">
								<Skeleton className="h-4 w-16" />
								<Skeleton className="h-8 w-24" />
							</div>
						</div>

						<Skeleton className="h-12 w-full rounded-lg mt-4" />
						<Skeleton className="h-3 w-3/4 mx-auto mt-3" />
					</div>
				</div>
			</div>

			<div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-background/95 backdrop-blur-xl border-t border-border/50 shadow-2xl safe-area-pb">
				<div className="max-w-lg mx-auto px-4 py-3 space-y-2">
					<div className="flex items-center justify-between gap-3">
						<div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
							<Skeleton className="h-9 w-9 sm:h-10 sm:w-10 rounded-lg shrink-0" />
							<div className="flex-1 min-w-0">
								<Skeleton className="h-4 w-28 mb-1" />
								<Skeleton className="h-3 w-24" />
							</div>
						</div>
						<Skeleton className="h-10 w-28 rounded-md shrink-0" />
					</div>
					<div>
						<div className="flex justify-between mb-1">
							<Skeleton className="h-3 w-20" />
							<Skeleton className="h-3 w-8" />
						</div>
						<Skeleton className="h-1 w-full rounded-full" />
					</div>
				</div>
			</div>
		</div>
	);
}
